import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app"
import { SystemSettingsWrapper } from "./styled"
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../assets";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { updateSystemSettingsService } from "../../../utils/api/configuration/systemsettings/updateSystemSettings";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseButton } from "../../../components/button/styled";
import { retrieveSystemSettingService } from "../../../utils/api/configuration/systemsettings/retrieveSystemSetting";

export const SystemSettings = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;
    // the idea is to show the user a minute more
    // than it's actually recognized on the system because
    // the system checks for refreshing a minute before expiration
    const options = [
        { value: 4, label: "3 minutes" },
        { value: 6, label: "5 minutes" },
        { value: 11, label: "10 minutes" },
        { value: 16, label: "15 minutes" },
    ];

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        timeout: " ",
        passwordExpires: "",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    useEffect(() => {
        retrieveSystemSettingService(TOKEN)
            .then(async (data) => {
                return setFormDetails({
                    timeout: data.timeout || " ",
                    passwordExpires: data.passwordExpires || "",
                });
            })
            .catch((err) => {
                console.error("Failed to fetch system setting:", err);
            });
    }, [TOKEN]);

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await updateSystemSettingsService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError(`Failed to update system settings. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update system settings. ${error.message}`);
            console.error(`Failed to update system settings:`, error);
        }
    };

    return (
        <AppLayout
            pageId={"System-Settings"}
            pageTitle={"System Settings"}
        >
            <SystemSettingsWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title={"System Settings Successfully Updated"}
                    message="System settings has been successfully updated."
                    callToAction="OK"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to update settings.
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Enable System Inactivity Logout after (In Minutes):
                                </BaseLabel>
                                <BaseSelect
                                    name="timeout"
                                    fontweight={400}
                                    value={formDetails.timeout}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Time
                                    </BaseOption>
                                    {options?.map((time: Record<string, any>, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={time.value}
                                        >
                                            {time.label}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Enable Password Expiration after (In Days):
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="passwordExpires"
                                    value={formDetails.passwordExpires}
                                    placeholder="Enter Days Count"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </SystemSettingsWrapper>
        </AppLayout>
    )
}